import { Flex } from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';
import { useEffect, useMemo, useRef } from 'react';

import { quickAddBrandIds } from '@features/Cashback/Calculator/constants/brands';
import { createCalcOutput } from '@features/Cashback/Calculator/utils/createCalcOutput';
import { RetailPartner } from '@generated/gql/graphql';
import { useDebounce } from '@hooks/useDebounce';
import { useSyncState } from '@hooks/useSyncState';
import { isNullish } from '@utils/isNullish';

import { useCashbackCalculatorContext } from '../CalculatorProvider/CalculatorContext';
import { SpendType } from '../types';
import { BrandEntry } from './BrandEntry/BrandEntry';
import { BrandQuickAdd } from './BrandQuickAdd/BrandQuickAdd';
import { BrandSearch } from './BrandSearch/BrandSearch';
import { type SpendBrandFields } from './types';
import { calcCategoriesFromRetailers } from './utils/calculateSpend';

const SpendBrand = () => {
  const quickAddInitialised = useRef(false);
  const { setOutput, retailers, retailersLoading, coreServiceCount } =
    useCashbackCalculatorContext();

  const brands = useMemo(
    () => [...retailers.partners, ...retailers.nonPartners],
    [retailers.partners, retailers.nonPartners]
  );

  const nonPartnerIds = useMemo(
    () => new Set(retailers.nonPartners.map((brand) => brand.id)),
    [retailers.nonPartners]
  );
  const { values, setFieldValue } = useFormikContext<SpendBrandFields>();

  const calcOutput = () => {
    const categories = calcCategoriesFromRetailers(
      selectedRetailers,
      values.cbcCalcBrandFields,
      {
        nonPartnerIds,
        coreServiceCount,
      }
    );
    const output = createCalcOutput(categories);
    setOutput(SpendType.Brand, output);
  };
  const debouncedCalc = useDebounce(calcOutput, 250);

  // since we're adding the retailers as Formik fields, via
  // their brand ID, we can simply look at the formik values
  // and filter the list of retailers by formik keys
  const selectedRetailers = useMemo((): RetailPartner[] => {
    return brands.filter((brand) =>
      Object.entries(values.cbcCalcBrandFields).find(
        ([field, value]) => brand.id === field && !isNullish(value)
      )
    );
  }, [brands, values]);

  const [quickAddBrands, setQuickAddBrands] = useSyncState<RetailPartner[]>(
    'cbcBrandsQuick',
    []
  );

  const initialQuickAddBrands = useMemo(() => {
    return brands.filter((brand) => quickAddBrandIds.includes(brand.id));
  }, [brands]);

  useEffect(() => {
    // we only want to populate quickadd once, when we get the
    // retailers, and hense "brands", initialized. We may want
    // to move this list elsewhere, if we give it some more
    // defined logic beyond picking random retailers.
    if (quickAddInitialised.current || !initialQuickAddBrands.length) return;

    quickAddInitialised.current = true;
    setQuickAddBrands(initialQuickAddBrands);
  }, [initialQuickAddBrands, setQuickAddBrands]);

  const handleSelect = (brandId: Nullable<string>) => {
    if (!brandId) return;
    if (selectedRetailers.some((brand) => brand.id === brandId)) return;
    const selectedBrand = brands.find((brand) => brand.id === brandId);
    if (!selectedBrand) return;
    setQuickAddBrands(quickAddBrands.filter((brand) => brand.id !== brandId));
    handleChangeRetailerValue(brandId, selectedBrand.avgMonthlySpend);
  };

  const handleRemoveRetailer = (brandId: string) => {
    setFieldValue(`cbcCalcBrandFields.${brandId}`, undefined);
    calcOutput();
  };

  const handleChangeRetailerValue = (brandId: string, value: number) => {
    setFieldValue(`cbcCalcBrandFields.${brandId}`, value);
  };

  // run output calculation each time the values change. This would then
  // include field value updates, as well as field additions and deletions
  // for adding/removing retailers from selection. We're debouncing this
  // to avoid expensive calls on each input change.
  useEffect(() => {
    debouncedCalc();
  }, [values, debouncedCalc]);

  return (
    <Flex direction="column" spacing="lg">
      <Flex direction="column" spacing="lg">
        {selectedRetailers.map((brand) => (
          <BrandEntry
            key={brand.id}
            brand={brand}
            value={values.cbcCalcBrandFields[brand.id]}
            onChange={handleChangeRetailerValue}
            onDelete={() => handleRemoveRetailer(brand.id)}
          />
        ))}
        <BrandSearch
          brands={brands}
          onSelect={handleSelect}
          onChange={(value) => setFieldValue('cbcCalcSpendBrandSearch', value)}
          inputValue={values.cbcCalcSpendBrandSearch}
          loading={retailersLoading}
        />
      </Flex>
      <BrandQuickAdd brands={quickAddBrands} onSelect={handleSelect} />
    </Flex>
  );
};

export { SpendBrand };
